/**
 * Document-numbering admin hooks (C-12). Backed by
 * /api/v1/document-numbering (real Postgres, document.numbering_schemes) —
 * rule-based per-type schemes (e.g. SOP-QA-001) that drive doc-number
 * allocation at creation time. The sequence counter (nextSeq) is
 * system-managed and never editable from here.
 */
import {
  useMutation, useQuery, useQueryClient,
  type UseMutationResult, type UseQueryResult,
} from '@tanstack/react-query';
import { apiFetch } from '../../api/client';

export interface NumberingScheme {
  schemeId: string;
  documentType: string;
  prefix: string;
  deptSegment: string | null;
  separator: string;
  padWidth: number;
  nextSeq: number;
  active: boolean;
  nextNumber: string;          // server-rendered preview of the next allocation
}
export interface NumberingListResp { items: NumberingScheme[]; total: number }

export function useNumberingSchemesQuery(): UseQueryResult<NumberingListResp> {
  return useQuery<NumberingListResp>({
    queryKey: ['document-numbering'],
    queryFn: ({ signal }) => apiFetch<NumberingListResp>('/api/v1/document-numbering', { signal }),
    staleTime: 30_000,
  });
}

export interface UpdateSchemeInput {
  prefix?: string;
  deptSegment?: string | null;   // present key clears (null) or sets the segment
  padWidth?: number;
  active?: boolean;
}
export function useUpdateScheme(documentType: string): UseMutationResult<NumberingScheme, Error, UpdateSchemeInput> {
  const qc = useQueryClient();
  return useMutation<NumberingScheme, Error, UpdateSchemeInput>({
    mutationFn: (body) =>
      apiFetch<NumberingScheme>(`/api/v1/document-numbering/${encodeURIComponent(documentType)}`, { method: 'PUT', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['document-numbering'] }),
  });
}

/** Client-side mirror of the server preview, so the edit dialog can show the
 *  resulting number live as the admin types (server stays the source of truth). */
export function previewNumber(s: Pick<NumberingScheme, 'prefix' | 'deptSegment' | 'separator' | 'padWidth' | 'nextSeq'>): string {
  const sep = s.separator || '-';
  const parts = [s.prefix || ''];
  if (s.deptSegment) parts.push(s.deptSegment);
  parts.push(String(s.nextSeq).padStart(Math.max(1, s.padWidth || 1), '0'));
  return parts.join(sep);
}
